/**
 * C-02 受注箱（FR-20）／C-04 受注一覧（FR-32）＝同じ表を条件だけ変えて出す。
 *
 * - 受注箱＝未確認のみ・**要確認を上位**・30秒ポーリング（05 §5.2）
 * - 受注一覧＝フィルタは **URLクエリ＝状態**（05 §5.2＝リロード・共有・戻るで再現する）
 * - チャネル＝FAX/メール/電話/アプリ・図面。**LINEトークは無い**（D-39）
 */

import { CHANNEL_LABEL, Badge, Card, Chip, ConfirmDialog, Empty, ErrorNote, formatDate, formatDateTime, PageHeader, Skeleton, statusTone, Table, Td } from "@kenzai/ui";
import { console_, type InboxRow } from "@kenzai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useIsMobile } from "../useIsMobile";

import { LineAlertChip, OrderAmount } from "./orderDisplay";

/**
 * 一覧からの進行操作（D-58＝出荷した／配送に出た／納品済みにする）。
 *
 * **活性判定はサーバの `can_*` をそのまま使う**（`flag` がそのプロパティ名）＝画面に状態機械を
 * 二重実装しない。testid は `deliver-` と前方一致しない名前にする（既存 E2E が
 * `[data-testid^="deliver-"]` の件数を見ているため・first-stretch.spec.ts）。
 */
type ProgressAction = "ship" | "out_for_delivery" | "deliver";

const PROGRESS: Record<
  ProgressAction,
  {
    label: string;
    testid: string;
    flag: "can_ship" | "can_out_for_delivery" | "can_deliver";
    confirmTitle: string;
    confirmBody: string;
    call: (id: number, updatedAt: string) => Promise<unknown>;
  }
> = {
  ship: {
    label: "出荷した",
    testid: "ship",
    flag: "can_ship",
    confirmTitle: "この注文を出荷済みにしますか？",
    confirmBody: "を出荷したこととして記録します。取引先には「出荷しました」の通知が届きます。",
    call: (id, at) => console_.ship(id, at),
  },
  out_for_delivery: {
    label: "配送に出た",
    testid: "otd",
    flag: "can_out_for_delivery",
    confirmTitle: "この注文を配送中にしますか？",
    confirmBody: "を配送に出たこととして記録します。取引先には「配送中です」の通知が届きます。",
    call: (id, at) => console_.outForDelivery(id, at),
  },
  deliver: {
    label: "納品済みにする",
    testid: "deliver",
    flag: "can_deliver",
    confirmTitle: "この注文を納品済みにしますか？",
    confirmBody: "をお届け済みとして記録します。取引先には「お届けしました」の通知が届きます。",
    call: (id, at) => console_.deliver(id, at),
  },
};

const PROGRESS_ORDER: ProgressAction[] = ["ship", "out_for_delivery", "deliver"];

/*
 * ★ボタンの見た目は**共有の `.kz-btn` 系**に寄せた（M1.7・S3）。
 *   ここには以前 `manualOrderLink`（44px）と `rowActionButton`（36px）という
 *   この画面だけの style オブジェクトがあり、`formStyles.ts` の 44/36px、
 *   `packages/ui` の `.kz-btn` と合わせて**同じ画面に3種類の高さ**が並んでいた。
 *   手入力の導線・行内の進行操作はどちらも「一覧の主要導線」＝`--flat`（淡い緑地）にする。
 */

/**
 * FAX 取込（FR-20〜22・S9b）＝**現在は停止中**（D-59・T-D22）。
 *
 * **なぜ押せなくするか**：AI 下書きを凍結している間、アップロードは成功して原文だけが溜まり、
 * 下書きは**永久に現れない**（ai-draft タスクを処理する側を止めている）。それでも押せてしまうと
 * 「取り込みました（AI が下書きを準備します）」と出るのに注文が受注箱に来ない＝**表示と実体が真逆**
 * になる（監査 demo#6 と同型で、いちばん事故る形）。
 *
 * 代わりに**手入力（C-17）**へ案内する＝FAX の原本を見てその場で起票すれば注文は必ず立つ。
 * backend の取込 API（`POST /console/inbox/fax`）は**消していない**＝解凍時にこの UI を戻すだけ。
 * 時期は約束しない（解凍は TBD-9 の法務裁定が入口＝いつとは言えない）。
 */
function FaxUpload() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "var(--kz-space-1)" }}>
      <span
        data-testid="fax-upload-frozen"
        aria-disabled="true"
        style={{
          minHeight: 40,
          display: "inline-flex",
          alignItems: "center",
          padding: "0 var(--kz-space-4)",
          border: "1px dashed var(--kz-color-border)",
          borderRadius: "var(--kz-radius-sm)",
          background: "var(--kz-color-surface-sunken)",
          color: "var(--kz-color-ink-muted)",
          fontWeight: 600,
          cursor: "not-allowed",
          whiteSpace: "nowrap",
        }}
      >
        FAXの読み取りは現在ご利用いただけません
      </span>
      <span style={{ fontSize: 11, color: "var(--kz-color-ink-muted)", maxWidth: 260, textAlign: "right" }}>
        FAXで受けたご注文は「電話・FAXを手入力」から登録してください。
      </span>
    </div>
  );
}

/**
 * C-04 **受注管理**（旧 C-02 受注箱 ＋ 旧 C-04 受注一覧を統合＝D-76）。
 *
 * ★**なぜ1画面にしたか**：受注箱と受注一覧は同じ表を条件だけ変えて出していたのに、
 * 名前が2つあるせいで「どちらを見ればよいか」を毎回考えることになっていた（stg 一巡 F6＝
 * 森井氏「ややこしいので受注一覧のみにして」）。
 *
 * ★**タブとチップの二重構造は作らない**（実装時の判断・2026-07-30）＝指示書は
 * 「未処理タブ／すべてタブ／状態別チップ」の3層だったが、それだと「未処理タブの中の未確認チップ」
 * のような意味の重なりが生まれる。**チップ1列**にして先頭に「未処理」を置き、
 * 未処理は他の状態と排他にした＝概念を増やさずに同じことができる。
 *
 * 「未処理」＝旧受注箱の性質をそのまま引き継ぐ：`status=未確認` ＋ **要確認を上位**
 * （`unresolved_first`・T-D48 で cursor もフラグ相を持つ）＋ 30秒ポーリング ＋ チャネル絞り込み
 * ＋ 手入力の導線。既定（何も選んでいない状態）は**すべて**＝「今どうなっているか」を一覧で掴む。
 */
export function Orders() {
  const [params, setParams] = useSearchParams();
  const isMobile = useIsMobile();
  const todo = params.get("tab") === "todo";
  const channel = params.get("channel") ?? "";

  // 「未処理」だけは URL を組み立てて投げる（画面の意味＝サーバのフィルタに1対1で対応させる）
  const qs = todo
    ? new URLSearchParams({
        status: "未確認",
        unresolved_first: "true",
        ...(channel ? { channel } : {}),
      }).toString()
    : params.toString();

  const q = useQuery({
    queryKey: ["orders", qs],
    queryFn: () => console_.orders(qs ? `?${qs}` : ""),
    // 05 §5.2：未処理は 30 秒ポーリング（M1 では SSE/WebSocket を持ち込まない＝過剰装備回避）。
    // すべて表示のときは回さない（履歴を眺めている画面を毎30秒作り直す理由がない）。
    refetchInterval: todo ? 30_000 : false,
  });

  return (
    <>
      <PageHeader
        title="受注管理"
        subtitle={todo ? "未処理の注文。要確認のあるものを上に出しています。" : "すべての注文"}
        actions={
          // ★**上端で揃える**（M1.7）＝ここは高さの違うものが並ぶ（手入力ボタン／FAXの2行の案内／
          //   チャネルの選択）。既定の伸縮のままだと、ボタンだけが一番高いものに合わせて
          //   間延びし、色の付いた面ほど「大きな板」に見える。
          <div style={{ display: "flex", gap: "var(--kz-space-2)", alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* C-17（T-D7）＝電話・FAX・メールで受けた注文をここから起票する。
                ★スマホでは**下部タブの「＋手入力」に譲る**（3つ横並びは 390px で必ず溢れる）。 */}
            {!isMobile && (
              <>
                <Link to="/orders/new" data-testid="manual-order-link" className="kz-btn kz-btn--flat">
                  電話・FAXを手入力
                </Link>
                {todo && <FaxUpload />}
              </>
            )}
            {todo && (
              <select
                className="kz-select"
                value={channel}
                data-testid="channel-filter"
                onChange={(e) => {
                  const next = new URLSearchParams({ tab: "todo" });
                  if (e.target.value) next.set("channel", e.target.value);
                  setParams(next);
                }}
                style={{
                  font: "inherit",
                  padding: "var(--kz-space-2)",
                  border: "1px solid var(--kz-color-border)",
                  borderRadius: "var(--kz-radius-sm)",
                  background: "var(--kz-color-surface)",
                }}
              >
                <option value="">すべてのチャネル</option>
                {/* D-39：**「LINEトーク」は無い**。fax/mail/tel は手入力（C-17）で入る */}
                {["web", "fax", "mail", "tel"].map((c) => (
                  <option key={c} value={c}>
                    {CHANNEL_LABEL[c]}
                  </option>
                ))}
              </select>
            )}
          </div>
        }
      />
      <StatusFilter />
      <List
        query={q}
        todoView={todo}
        emptyTitle={todo ? "未処理の注文はありません" : "該当する注文はありません"}
        emptyHint={todo ? "新しい注文が届くとここに出ます。" : "条件を変えてお試しください。"}
      />
    </>
  );
}

/** フィルタは URL に入れる（05 §5.2）＝リロードしても・共有しても・戻っても同じ画面。 */
function StatusFilter() {
  const [params, setParams] = useSearchParams();
  const todo = params.get("tab") === "todo";
  const current = params.getAll("status");
  const bin = params.get("bin_id");

  return (
    <div style={{ display: "flex", gap: "var(--kz-space-2)", marginBottom: "var(--kz-space-4)", flexWrap: "wrap" }}>
      {/* ★先頭は「未処理」＝旧 受注箱（D-76）。**他の状態と排他**＝要確認の上位ソートと
          30秒ポーリングが効く特別なモードなので、他のチップと重ねると意味が曖昧になる。 */}
      <Chip
        on={todo}
        data-testid="filter-未処理"
        onClick={() => setParams(todo ? new URLSearchParams() : new URLSearchParams({ tab: "todo" }))}
      >
        未処理
      </Chip>
      <span style={{ alignSelf: "center", color: "var(--kz-color-border)" }}>／</span>
      {/* 卸の業務語彙（LC-08 の左）。**5状態＋取消**＝M1 で到達するのは未確認/確認済/取消 */}
      {["未確認", "確認済", "出荷指示済", "配送中", "納品済", "取消"].map((s) => {
        const on = !todo && current.includes(s);
        return (
          <Chip
            key={s}
            on={on}
            data-testid={`filter-${s}`}
            onClick={() => {
              // 状態を選んだら「未処理」モードから抜ける（排他）
              const next = new URLSearchParams(params);
              next.delete("tab");
              next.delete("channel");
              next.delete("status");
              // 「未処理」から来たときは、そこで持っていた status（未確認）を引き継がない
              const kept = todo ? [] : current;
              (on ? kept.filter((x) => x !== s) : [...kept, s]).forEach((x) => next.append("status", x));
              setParams(next);
            }}
          >
            {s}
          </Chip>
        );
      })}
      {bin && (
        <Chip
          on
          style={{ marginLeft: "auto" }}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.delete("bin_id");
            setParams(next);
          }}
        >
          配送便で絞り込み中 ✕
        </Chip>
      )}
      {/* ピッキングリスト（FR-46・D-76・M1.5）＝**便で絞っているときだけ**出す。
          便を決めずには出せない紙なので、絞っていないときにボタンだけ置くと押せない入口になる。 */}
      {bin && <PickingListBar binId={Number(bin)} />}
    </div>
  );
}

/** JST の今日（`YYYY-MM-DD`）＝ブラウザのタイムゾーンに依らず日本の日付を出す。 */
function jstToday(): string {
  return new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

/**
 * ピッキングリストの発行（FR-46）。**便＋日付**で内容が一意に定まる（番号を持たない）。
 *
 * ★日付を既定の「今日」で決め打ちにしない＝倉庫は**明日ぶんを前日に**作ることが多い。
 *   既定は今日にしつつ、その場で変えられるようにする。
 */
function PickingListBar({ binId }: { binId: number }) {
  const [date, setDate] = useState(jstToday);
  const print = useMutation({ mutationFn: () => console_.printPickingList(binId, date) });

  return (
    <div
      style={{
        flexBasis: "100%",
        display: "flex",
        alignItems: "center",
        gap: "var(--kz-space-2)",
        marginTop: "var(--kz-space-2)",
      }}
    >
      <label style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
        出荷日
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="picking-date"
          style={{
            marginLeft: "var(--kz-space-2)",
            padding: "6px 8px",
            borderRadius: "var(--kz-radius-sm)",
            border: "1px solid var(--kz-color-border)",
            background: "var(--kz-color-surface)",
            color: "var(--kz-color-ink)",
            font: "inherit",
            fontSize: "var(--kz-font-size-md)",
          }}
        />
      </label>
      <button
        type="button"
        className="kz-btn"
        onClick={() => print.mutate()}
        disabled={print.isPending}
        data-testid="print-picking-list"
      >
        {print.isPending ? "発行しています…" : "ピッキングリストを印刷"}
      </button>
      <ErrorNote error={print.error} />
    </div>
  );
}

/**
 * スマホの受注1件＝**カード**（D-68・FR-120）。
 *
 * 表の8〜9列と**同じ情報**を、外出先で見る順に組み替える＝
 * ①今どうなっているか（状態・要確認）②何の注文か（番号・チャネル・取引先/現場/発注者）
 * ③いつ・いくら（希望日/便・点数・金額）④次の一手（進行ボタン）。
 *
 * ★ testid は**カード専用の名前**にする（`row-{id}` や `deliver-{id}` を再利用すると、
 *   デスクトップ版と合わせて件数が倍になり、`[data-testid^="deliver-"]` の最初の1つを押す
 *   既存 E2E が別の要素を掴む）。ここは排他レンダリングなので実際には重複しないが、
 *   **名前で区別しておけば将来同時に出しても壊れない**。
 */
function OrderCard({ row: r, onAdvance }: { row: InboxRow; onAdvance: (a: ProgressAction) => void }) {
  const actions = PROGRESS_ORDER.filter((a) => r[PROGRESS[a].flag]);
  return (
    <Card padding="0">
      <div
        data-testid={`order-card-${r.order.id}`}
        style={{ padding: "var(--kz-space-4)", display: "grid", gap: "var(--kz-space-2)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
          <Badge tone={statusTone(r.order.status_cache)}>{r.order.status_cache}</Badge>
          {r.open_flag_count > 0 && <Badge tone="warn">要確認 {r.open_flag_count}</Badge>}
          {r.open_request_count > 0 && <Badge tone="primary">依頼 {r.open_request_count}</Badge>}
          {/* FR-130・D-89：LINE が届いていない可能性。**testid に `order-card-` を使わない**＝
              mobile-console.spec.ts が `[data-testid^="order-card-"]` でカードを数えており、
              同じ前置きを足すとカードが増えたように見える。 */}
          {r.line_alert && <LineAlertChip testId={`card-line-alert-${r.order.id}`} />}
          <Badge>{CHANNEL_LABEL[r.order.channel] ?? r.order.channel}</Badge>
          <span
            className="kz-num"
            style={{ marginLeft: "auto", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}
          >
            {formatDateTime(r.order.received_at)}
          </span>
        </div>

        {/* ★カード全体ではなくこのリンクを押させる＝カード全体をリンクにすると、
            中の進行ボタンを押したつもりが詳細へ飛ぶ事故が起きる */}
        <Link
          to={`/orders/${r.order.id}`}
          data-testid={`order-card-link-${r.order.id}`}
          className="kz-num"
          style={{
            color: "var(--kz-color-primary)",
            fontWeight: 700,
            fontSize: "var(--kz-font-size-lg)",
            textDecoration: "none",
            minHeight: 44,
            display: "flex",
            alignItems: "center",
          }}
        >
          {r.order.display_no}
        </Link>

        <div>
          <div>{r.customer_name ?? <Muted>確認中</Muted>}</div>
          <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
            {r.site_name ?? "現場 確認中"}
          </div>
          {r.orderer_name && (
            <div
              data-testid={`order-card-orderer-${r.order.id}`}
              style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}
            >
              発注 {r.orderer_name}
              {!r.orderer_name_is_registered && "（LINE の表示名）"}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: "var(--kz-space-3)",
            flexWrap: "wrap",
            fontSize: "var(--kz-font-size-sm)",
            color: "var(--kz-color-ink-muted)",
          }}
        >
          <span>
            納品希望 <span className="kz-num">{formatDate(r.order.desired_date)}</span>
            {r.bin_name && ` ${r.bin_name}`}
          </span>
          <span>
            <span className="kz-num">{r.item_count}</span> 点
          </span>
          {/* FR-131・D-90：配送料が別途の卸は「商品計＋配送料」で出す（込みの卸は従来どおり） */}
          <span style={{ marginLeft: "auto", color: "var(--kz-color-ink)", fontWeight: 700, textAlign: "right" }}>
            <OrderAmount itemsTotal={r.total_amount} deliveryFee={r.order.delivery_fee} />
          </span>
        </div>

        {actions.length > 0 && (
          <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
            {actions.map((a) => (
              <button
                key={a}
                type="button"
                data-testid={`card-${PROGRESS[a].testid}-${r.order.id}`}
                onClick={() => onAdvance(a)}
                // タップ44px（FR-120）＝表の 36px はそのまま（デスクトップの見た目を変えない）
                className="kz-btn kz-btn--flat"
                style={{ flex: "1 1 auto" }}
              >
                {PROGRESS[a].label}
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function List({
  query,
  emptyTitle,
  emptyHint,
  todoView = false,
}: {
  todoView?: boolean;
  query: { isLoading: boolean; isError: boolean; error: unknown; data?: { items: InboxRow[] } };
  emptyTitle: string;
  emptyHint: string;
}) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  // FR-103 改（D-54①）＋D-58：一覧から1クリックで進行（出荷した／配送に出た／納品済みにする）。
  // 確認ダイアログ（NFR-04）を挟む対象の行と、どの操作かを持つ。
  const [target, setTarget] = useState<{ row: InboxRow; action: ProgressAction } | null>(null);
  const advance = useMutation({
    mutationFn: ({ row, action }: { row: InboxRow; action: ProgressAction }) =>
      PROGRESS[action].call(row.order.id, row.order.updated_at),
    onSettled: () => {
      // 成功でも失敗でもダイアログは閉じて再取得する（失敗＝別の人が先に納品/取消した等は
      // 一覧が真の状態に直る＝自己修復）。失敗の理由は下の ErrorNote で見せる。
      setTarget(null);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  // ★ロード中は**これから出る一覧の形**で待たせる（M1.7）。文字1行だと、データ到着の瞬間に
  //   高さが跳ねて目が迷子になる。
  //   ★**`<table>` も testid 付きの要素も置かない**＝E2E に「table が見えた＝データが確定した」を
  //     前提に本文を読む検査がある（multivendor.spec.ts）。器だけ先に出すと空の中身を読んで落ちる。
  //     ここは元から「ロード中は table を描かない」構造なので、その性質をそのまま保つ。
  if (query.isLoading)
    return (
      <Card padding="var(--kz-space-4)">
        <Skeleton rows={6} height={22} />
      </Card>
    );
  if (query.isError) return <ErrorNote error={query.error} />;
  const rows = query.data?.items ?? [];
  if (rows.length === 0) return <Empty title={emptyTitle} hint={emptyHint} />;
  // 一覧に進められる行が1つでもあれば「操作」列を出す（受注箱＝未確認のみ＝出ない）
  const hasDeliverable = rows.some((r) => r.can_ship || r.can_out_for_delivery || r.can_deliver);

  // 確認ダイアログはカード版・表版で**同じもの**を使う（文言と busy の扱いを二重に持たない）
  const dialog = target && (
    <ConfirmDialog
      title={PROGRESS[target.action].confirmTitle}
      body={
        <>
          <span className="kz-num">{target.row.order.display_no}</span>{" "}
          {PROGRESS[target.action].confirmBody}
        </>
      }
      confirmLabel={PROGRESS[target.action].label}
      busy={advance.isPending}
      onConfirm={() => advance.mutate(target)}
      onCancel={() => setTarget(null)}
    />
  );

  // ★スマホは**カード**（D-68・FR-120）＝8〜9列の表を 390px に押し込むと、横スクロールか
  //   文字の潰れのどちらかになる。**デスクトップの表はそのまま**＝同時に DOM へ置かず
  //   条件分岐で描き分ける（testid が2つになると既存 E2E が strict mode で落ちる）。
  if (isMobile) {
    return (
      <>
        {advance.isError && <ErrorNote error={advance.error} />}
        <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>
          {rows.map((r) => (
            <OrderCard key={r.order.id} row={r} onAdvance={(action) => setTarget({ row: r, action })} />
          ))}
        </div>
        {dialog}
      </>
    );
  }

  return (
    <>
      {advance.isError && <ErrorNote error={advance.error} />}
    <Card padding="0">
      <Table
        head={["受付", "番号", "チャネル", "取引先／現場／発注者", "納品希望", "明細", "金額", "状態", ...(hasDeliverable ? ["操作"] : [])]}
      >
        {rows.map((r) => (
          <tr key={r.order.id} data-testid={`row-${r.order.id}`}>
            <Td style={{ whiteSpace: "nowrap" }}>
              <span className="kz-num">{formatDateTime(r.order.received_at)}</span>
            </Td>
            <Td>
              <Link
                // 戻り先を保つ（未処理から開いたか、すべてから開いたか）＝D-76
                to={`/orders/${r.order.id}${todoView ? "" : "?from=all"}`}
                className="kz-num"
                style={{ color: "var(--kz-color-primary)", fontWeight: 600 }}
              >
                {r.order.display_no}
              </Link>
            </Td>
            <Td>
              <Badge>{CHANNEL_LABEL[r.order.channel] ?? r.order.channel}</Badge>
            </Td>
            <Td>
              <div>{r.customer_name ?? <Muted>確認中</Muted>}</div>
              <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
                {r.site_name ?? "現場 確認中"}
              </div>
              {/* T-D25：誰からの注文か。**列は増やさない**＝この表はすでに8〜9列あり、
                  1列足すと 1280px で必ず横スクロールになる。「取引先（会社）／現場／発注者（人）」は
                  同じ問い（これは誰の、どこ向けの注文か）への答えなので1セルに畳む。
                  電話・FAX の手入力は発注者がいない＝**行ごと出さない**（「—」を全行に並べない）。 */}
              {r.orderer_name && (
                <div
                  data-testid={`orderer-${r.order.id}`}
                  style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}
                >
                  {/* **「さん」を付けない**＝ここに出るのは本人の登録名とは限らず（LINE の表示名・
                      屋号のことがある）、敬称込みの登録名だと二重敬称になる。代わりに
                      「発注」と書いて、得意先・現場の行と役割で見分けられるようにする。 */}
                  発注 {r.orderer_name}
                  {!r.orderer_name_is_registered && "（LINE の表示名）"}
                </div>
              )}
            </Td>
            <Td style={{ whiteSpace: "nowrap" }}>
              <span className="kz-num">{formatDate(r.order.desired_date)}</span>
              {r.bin_name && (
                <span style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
                  {" "}
                  {r.bin_name}
                </span>
              )}
            </Td>
            <Td>
              <span className="kz-num">{r.item_count}</span> 点
            </Td>
            {/* FR-131・D-90：**列は増やさない**（既に8〜9列で 1280px が限界＝T-D25）。
                金額セルの中に内訳を畳む＝「◯◯円（うち配送料 ◯◯円）」。 */}
            <Td style={{ whiteSpace: "nowrap" }}>
              <OrderAmount itemsTotal={r.total_amount} deliveryFee={r.order.delivery_fee} />
            </Td>
            <Td>
              <div style={{ display: "flex", gap: "var(--kz-space-1)", flexWrap: "wrap" }}>
                <Badge tone={statusTone(r.order.status_cache)}>{r.order.status_cache}</Badge>
                {/* 要確認＝**未解消のフラグがある**（04 §3）。承認できない理由がここで見える */}
                {r.open_flag_count > 0 && (
                  <Badge tone="warn">要確認 {r.open_flag_count}</Badge>
                )}
                {/* D-77：この受注に紐づく**未完了の指示・依頼**。押すと発注管理をその受注に絞る
                    ＝「誰かに頼んだはずだが、どうなった？」を一覧から辿れる。 */}
                {r.open_request_count > 0 && (
                  <Link
                    to={`/requests?order_id=${r.order.id}`}
                    data-testid={`row-requests-${r.order.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Badge tone="primary">依頼 {r.open_request_count}</Badge>
                  </Link>
                )}
                {/* FR-130・D-89：LINE 未達。**状態の隣**に置く＝「この注文は今どうなっているか」を
                    読む場所で「相手に伝わっていないかもしれない」まで分かる（判定はサーバが配る）。 */}
                {r.line_alert && <LineAlertChip testId={`row-line-alert-${r.order.id}`} />}
              </div>
            </Td>
            {hasDeliverable && (
              <Td>
                {/* FR-103 改・D-58：**サーバの can_* をそのまま使う**（画面が status を再判定しない） */}
                <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
                  {PROGRESS_ORDER.filter((a) => r[PROGRESS[a].flag]).map((a) => (
                    <button
                      key={a}
                      type="button"
                      data-testid={`${PROGRESS[a].testid}-${r.order.id}`}
                      onClick={() => setTarget({ row: r, action: a })}
                      className="kz-btn kz-btn--flat kz-btn--sm"
                    >
                      {PROGRESS[a].label}
                    </button>
                  ))}
                </div>
              </Td>
            )}
          </tr>
        ))}
      </Table>
      {target && (
        <ConfirmDialog
          title={PROGRESS[target.action].confirmTitle}
          body={
            <>
              <span className="kz-num">{target.row.order.display_no}</span>{" "}
              {PROGRESS[target.action].confirmBody}
            </>
          }
          confirmLabel={PROGRESS[target.action].label}
          busy={advance.isPending}
          onConfirm={() => advance.mutate(target)}
          onCancel={() => setTarget(null)}
        />
      )}
    </Card>
    </>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span style={{ color: "var(--kz-color-ink-muted)" }}>{children}</span>;
}
